import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal } from 'antd';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import ChangePassword from './ChangePassword';

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;
if (!FUNCTIONS_BASE_URL) {
  throw new Error(
    'REACT_APP_FUNCTIONS_BASE_URL is not set. Copy frontend/.env.example to frontend/.env.local and fill in the values.'
  );
}

const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 500;

const BLOCKED_TYPES = new Set([
  '.exe', '.msi', '.bat', '.cmd', '.com', '.scr', '.pif',
  '.vbs', '.wsf', '.hta', '.ps1', '.ps2',
  '.pkg', '.dmg', '.command',
  '.apk', '.ipa',
  '.jar', '.class',
  '.php', '.asp', '.aspx', '.jsp', '.cgi', '.cfm',
]);

function isBlockedType(filename) {
  const ext = '.' + filename.split('.').pop().toLowerCase();
  return BLOCKED_TYPES.has(ext);
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Inline SVG icons ────────────────────────────────────────────────────────

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 22, height: 22, stroke: 'var(--orange)' }}>
    <polyline points="16 16 12 12 8 16"/>
    <line x1="12" y1="12" x2="12" y2="21"/>
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 20, height: 20, stroke: 'var(--orange)' }}>
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
    <polyline points="13 2 13 9 20 9"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 28, height: 28, stroke: 'var(--orange)' }}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, stroke: 'var(--orange)', flexShrink: 0, marginTop: 2 }}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 26, height: 26, stroke: '#aaa' }}>
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const WarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 17, height: 17, flexShrink: 0, marginTop: 1 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const ErrIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 17, height: 17, flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15 }}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const KeyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 14, height: 14 }}>
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);

// ── Shared sub-components ───────────────────────────────────────────────────

function PageHeader({ user, onSignOut, onChangePw }) {
  return (
    <header style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 24px', height: 56,
      background: 'var(--surface)',
      borderBottom: '1px solid var(--line)',
    }}>
      <img src="/assets/default-logo.svg" alt="TalentMapper" style={{ height: 24, width: 'auto' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>{user.email}</span>
        <button
          onClick={onChangePw}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: 'var(--mid)', padding: '4px 8px',
            borderRadius: 3,
          }}
        >
          <KeyIcon />
          Change password
        </button>
        <button
          onClick={onSignOut}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: 'var(--mid)', padding: '4px 8px',
            borderRadius: 3,
          }}
        >
          <LogoutIcon />
          Sign out
        </button>
      </div>
    </header>
  );
}

function LegalFooter() {
  return (
    <footer style={{
      marginTop: 28, marginBottom: 40,
      width: '100%', maxWidth: 520,
      margin: '28px auto 40px',
      padding: '0 20px',
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: 12, color: 'var(--mid)',
        lineHeight: 1.6, marginBottom: 10,
        padding: '10px 14px',
        background: 'var(--white)',
        borderRadius: 4,
        borderTop: '2px solid var(--orange)',
      }}>
        By uploading files you confirm you are authorised to share this data with TalentMapper and consent to it being
        processed in accordance with our{' '}
        <a href="https://thetalentmapper.com/privacy-policy" target="_blank" rel="noopener noreferrer"
          style={{ color: 'var(--orange)', textDecoration: 'none' }}>Privacy Policy</a>
        {' '}and{' '}
        <a href="https://thetalentmapper.com/terms-conditions" target="_blank" rel="noopener noreferrer"
          style={{ color: 'var(--orange)', textDecoration: 'none' }}>Data Processing Agreement</a>.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 14px', marginBottom: 8 }}>
        <a href="https://thetalentmapper.com/terms-conditions" target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>T&amp;Cs</a>
        <span style={{ fontSize: 12, color: 'var(--line)' }}>·</span>
        <a href="https://thetalentmapper.com/privacy-policy" target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>Privacy Policy</a>
        <span style={{ fontSize: 12, color: 'var(--line)' }}>·</span>
        <a href="https://thetalentmapper.com/cookies" target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>Cookies</a>
      </div>
      <p style={{ fontSize: 11, color: 'var(--faint)', margin: 0, lineHeight: 1.7 }}>
        TalentMapper Limited &middot; Company No. 14476153 &middot; Registered in England &amp; Wales &middot;{' '}
        Lewis Building, Bull Street, Spaces, Birmingham, B4 6AF
      </p>
    </footer>
  );
}

function SecurityNotice() {
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start',
      background: 'var(--almond)', borderRadius: 3,
      padding: '12px 14px', marginTop: 16,
    }}>
      <ShieldIcon />
      <p style={{ fontSize: 13, color: 'var(--black)', margin: 0, lineHeight: 1.55 }}>
        Your file is encrypted in transit and stored securely. Only your TalentMapper contact can access it,
        and it's deleted after 60 days. Questions?{' '}
        <a href="mailto:support@thetalentmapper.com" style={{ color: 'var(--orange)', textDecoration: 'none' }}>
          support@thetalentmapper.com
        </a>
      </p>
    </div>
  );
}

// ── Upload states: idle | selected | uploading | success | failed | expired ─

// Upload states: idle | selected | uploading | success | failed | expired
function FileUpload({ user }) {
  const [uploadState, setUploadState] = useState('idle');
  const [selectedFile, setSelectedFile] = useState(null);
  const [senderName, setSenderName] = useState('');
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState(0);
  const [slowConnection, setSlowConnection] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadError, setUploadError] = useState(null);

  const [wrongTypeModal, setWrongTypeModal] = useState(false);
  const [tooLargeModal, setTooLargeModal] = useState(false);
  const [removeConfirmModal, setRemoveConfirmModal] = useState(false);
  const [leaveModal, setLeaveModal] = useState(false);
  const [pendingLeave, setPendingLeave] = useState(null);
  const [changePwModal, setChangePwModal] = useState(false);

  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);
  const slowTimerRef = useRef(null);
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploadState === 'uploading') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadState]);

  const validateFile = useCallback((file) => {
    if (isBlockedType(file.name)) { setWrongTypeModal(true); return false; }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) { setTooLargeModal(true); return false; }
    return true;
  }, []);

  const handleFileSelect = useCallback((file) => {
    if (!validateFile(file)) return;
    setSelectedFile(file);
    setUploadState('selected');
  }, [validateFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
    e.target.value = '';
  };

  const handleRemoveFile = () => setRemoveConfirmModal(true);

  const confirmRemove = () => {
    setSelectedFile(null);
    setUploadState('idle');
    setRemoveConfirmModal(false);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploadState('uploading');
    setProgress(0);
    setSlowConnection(false);
    setUploadError(null);

    slowTimerRef.current = setTimeout(() => setSlowConnection(true), 8000);

    try {
      const token = await auth.currentUser.getIdToken();

      const res = await fetch(`${FUNCTIONS_BASE_URL}/generateUploadUrl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: selectedFile.name,
          contentType: selectedFile.type || 'application/octet-stream',
          senderName: senderName.trim(),
          note: note.trim(),
        }),
      });

      // 401 / 403 from generateUploadUrl → link expired / no access
      if (res.status === 401 || res.status === 403) {
        clearTimeout(slowTimerRef.current);
        setUploadState('expired');
        return;
      }

      if (!res.ok) {
        throw new Error(`Failed to get upload URL (${res.status})`);
      }

      const { uploadUrl } = await res.json();

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', selectedFile.type || 'application/octet-stream');

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(selectedFile);
      });

      clearTimeout(slowTimerRef.current);
      setUploadedFileName(selectedFile.name);
      setUploadState('success');
    } catch (err) {
      clearTimeout(slowTimerRef.current);
      setUploadError(err.message);
      setUploadState('failed');
    }
  };

  const handleRetry = () => {
    setUploadState('selected');
    setProgress(0);
    setSlowConnection(false);
    setUploadError(null);
  };

  const handleStartOver = () => {
    setSelectedFile(null);
    setSenderName('');
    setNote('');
    setUploadState('idle');
    setProgress(0);
    setSlowConnection(false);
    setUploadError(null);
  };

  const handleSignOut = async () => {
    if (uploadState === 'uploading') {
      setLeaveModal(true);
      setPendingLeave('signout');
      return;
    }
    await signOut(auth);
  };

  const confirmLeave = async () => {
    if (xhrRef.current) xhrRef.current.abort();
    setLeaveModal(false);
    if (pendingLeave === 'signout') await signOut(auth);
  };

  // ── State renderers ───────────────────────────────────────────────────────

  const renderSuccess = () => (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <div style={{
        width: 60, height: 60, background: 'var(--almond)', borderRadius: '50%',
        margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CheckIcon />
      </div>
      <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 500, color: 'var(--black)', marginBottom: 8 }}>
        File sent successfully
      </h2>
      <p style={{ fontSize: 14, color: 'var(--mid)', lineHeight: 1.6, marginBottom: 24 }}>
        <strong>{uploadedFileName}</strong> has been received securely by TalentMapper.
        Your contact will be in touch shortly.
      </p>
      <p style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 28 }}>
        Files are automatically deleted after 60 days.
      </p>
      <button
        onClick={handleStartOver}
        style={{
          padding: '12px 32px',
          fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
          background: 'var(--orange)', color: 'var(--white)',
          border: 'none', borderRadius: 3, cursor: 'pointer',
          transition: 'background .15s',
        }}
        onMouseEnter={(e) => { e.target.style.background = 'var(--orange-dark)'; }}
        onMouseLeave={(e) => { e.target.style.background = 'var(--orange)'; }}
      >
        Send another file
      </button>
    </div>
  );

  const renderExpired = () => (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <div style={{
        width: 56, height: 56, background: '#f0ede8', borderRadius: '50%',
        margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <ClockIcon />
      </div>
      <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 500, color: 'var(--black)', marginBottom: 8 }}>
        Link expired
      </h2>
      <p style={{ fontSize: 14, color: 'var(--mid)', lineHeight: 1.6, marginBottom: 24 }}>
        Your upload link is no longer valid. This usually means your session has expired or access has been
        removed. Please contact your TalentMapper contact to get a new link.
      </p>
      <a
        href="mailto:support@thetalentmapper.com"
        style={{
          display: 'inline-block', padding: '12px 32px',
          fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
          background: 'var(--orange)', color: 'var(--white)',
          textDecoration: 'none', borderRadius: 3,
          transition: 'background .15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--orange-dark)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--orange)'; }}
      >
        Contact support
      </a>
    </div>
  );

  const renderUploading = () => (
    <div style={{ padding: '8px 0' }}>
      {slowConnection && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          padding: '12px 14px', borderRadius: 3, marginBottom: 16,
          background: 'var(--warn-bg)',
        }}>
          <WarnIcon style={{ stroke: 'var(--warn-txt)' }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn-txt)', marginBottom: 2 }}>
              Slow connection
            </div>
            <div style={{ fontSize: 12, color: 'var(--warn-txt)', lineHeight: 1.5, opacity: .85 }}>
              Your file is still uploading — please don't close this window.
            </div>
          </div>
        </div>
      )}
      {/* Locked file row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 44, height: 44, background: 'var(--almond)',
          borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <FileIcon />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', marginBottom: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedFile?.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{selectedFile && formatBytes(selectedFile.size)}</div>
        </div>
      </div>
      {/* Progress bar */}
      <div style={{ background: '#f0ede8', borderRadius: 2, height: 3, margin: '14px 0 4px', overflow: 'hidden' }}>
        <div style={{
          height: 3, background: 'var(--orange)', borderRadius: 2,
          width: `${progress}%`, transition: 'width .3s var(--ease)',
        }} />
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{progress}% — do not close this window</div>
      <SecurityNotice />
    </div>
  );

  const renderFailed = () => (
    <div>
      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start',
        padding: '12px 14px', borderRadius: 3, marginBottom: 16,
        background: 'var(--err-bg)',
      }}>
        <ErrIcon />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--err-txt)', marginBottom: 2 }}>
            Upload failed
          </div>
          <div style={{ fontSize: 12, color: 'var(--err-txt)', lineHeight: 1.5, opacity: .85 }}>
            {uploadError || 'Something went wrong while uploading your file.'}
          </div>
        </div>
        <button
          onClick={handleRetry}
          style={{
            marginLeft: 'auto', flexShrink: 0,
            fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
            color: 'var(--white)', background: 'var(--err-txt)',
            border: 'none', borderRadius: 3, padding: '6px 14px', cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
      <button
        onClick={handleStartOver}
        style={{
          width: '100%', padding: 12,
          fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 500,
          background: 'var(--white)', border: '1.5px solid var(--black)',
          borderRadius: 3, color: 'var(--black)', cursor: 'pointer',
          transition: 'background .12s',
        }}
        onMouseEnter={(e) => { e.target.style.background = 'var(--almond)'; }}
        onMouseLeave={(e) => { e.target.style.background = 'var(--white)'; }}
      >
        Start over
      </button>
    </div>
  );

  const renderIdleSelected = () => {
    const isSelected = uploadState === 'selected' && selectedFile;
    const canSubmit = isSelected && senderName.trim();

    return (
      <div>
        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !isSelected && fileInputRef.current?.click()}
          style={{
            border: `1.5px dashed ${dragOver ? 'var(--orange)' : 'var(--line)'}`,
            borderRadius: 4,
            padding: isSelected ? '16px 20px' : '36px 24px',
            textAlign: 'center',
            cursor: isSelected ? 'default' : 'pointer',
            background: dragOver ? '#fffaf6' : 'var(--white)',
            transition: 'border-color .15s, background .15s',
          }}
        >
          {isSelected ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left' }}>
              <div style={{
                width: 44, height: 44, background: 'var(--almond)',
                borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <FileIcon />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', marginBottom: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFile.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{formatBytes(selectedFile.size)}</div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
                style={{
                  marginLeft: 'auto', background: 'none', border: 'none',
                  fontSize: 24, color: '#ccc', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0,
                }}
                aria-label="Remove file"
              >
                &times;
              </button>
            </div>
          ) : (
            <div>
              <div style={{
                width: 48, height: 48, background: 'var(--almond)',
                borderRadius: '50%', margin: '0 auto 14px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <UploadIcon />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', marginBottom: 4 }}>
                Drag your file here
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                or click to browse
              </div>
              <button
                type="button"
                style={{
                  fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 500,
                  color: 'var(--black)', background: 'var(--white)',
                  border: '1.5px solid var(--black)', borderRadius: 3,
                  padding: '7px 20px', cursor: 'pointer',
                  transition: 'background .12s',
                }}
                onMouseEnter={(e) => { e.target.style.background = 'var(--almond)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'var(--white)'; }}
              >
                Choose file
              </button>
              <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 12, letterSpacing: '.3px' }}>
                All file types accepted · Max {MAX_FILE_SIZE_MB} MB
              </div>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept="*"
          onChange={handleInputChange}
        />

        {/* Sender name */}
        <div style={{ marginTop: 20, marginBottom: 12 }}>
          <label style={{
            display: 'block', fontSize: 11, fontWeight: 600,
            color: 'var(--muted)', letterSpacing: '.6px', textTransform: 'uppercase',
            marginBottom: 6,
          }}>
            Your name <span style={{ fontWeight: 400, textTransform: 'none' }}>(so we know who sent it)</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Jane Smith, Company Name Ltd."
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            style={{
              width: '100%', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--black)',
              padding: '10px 12px', border: '1.5px solid var(--line)',
              borderRadius: 3, background: 'var(--white)', outline: 'none',
              transition: 'border-color .15s',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--orange)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--line)'; }}
          />
        </div>

        {/* Note */}
        <div style={{ marginBottom: 0 }}>
          <label style={{
            display: 'block', fontSize: 11, fontWeight: 600,
            color: 'var(--muted)', letterSpacing: '.6px', textTransform: 'uppercase',
            marginBottom: 6,
          }}>
            Note <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
          </label>
          <textarea
            placeholder="Any context about this file…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            style={{
              width: '100%', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--black)',
              padding: '10px 12px', border: '1.5px solid var(--line)',
              borderRadius: 3, background: 'var(--white)', outline: 'none',
              resize: 'vertical', transition: 'border-color .15s',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--orange)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--line)'; }}
          />
        </div>

        <SecurityNotice />

        <button
          onClick={handleUpload}
          disabled={!canSubmit}
          style={{
            width: '100%', marginTop: 20, padding: 13,
            fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
            background: canSubmit ? 'var(--orange)' : '#f0c4a0',
            color: 'var(--white)', border: 'none', borderRadius: 3,
            cursor: canSubmit ? 'pointer' : 'default',
            letterSpacing: '.2px', transition: 'background .15s',
          }}
          onMouseEnter={(e) => { if (canSubmit) e.target.style.background = 'var(--orange-dark)'; }}
          onMouseLeave={(e) => { if (canSubmit) e.target.style.background = 'var(--orange)'; }}
        >
          Send file securely
        </button>
        {isSelected && !senderName.trim() && (
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, textAlign: 'center' }}>
            Please enter your name to continue
          </p>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (uploadState === 'success') return renderSuccess();
    if (uploadState === 'expired') return renderExpired();
    if (uploadState === 'uploading') return renderUploading();
    if (uploadState === 'failed') return renderFailed();
    return renderIdleSelected();
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--surface)' }}>
      <PageHeader user={user} onSignOut={handleSignOut} onChangePw={() => setChangePwModal(true)} />

      <main style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '40px 20px 0',
      }}>
        <div style={{
          background: 'var(--white)',
          width: '100%', maxWidth: 520,
          borderRadius: 6, overflow: 'hidden',
          animation: 'fadeUp .35s var(--ease) both',
          boxShadow: '0 1px 4px rgba(33,33,33,.07)',
        }}>
          <div style={{ padding: '36px 44px 40px' }}>
            {uploadState !== 'success' && uploadState !== 'expired' && (
              <>
                <h1 style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 500,
                  color: 'var(--black)', marginBottom: 6, marginTop: 0 }}>
                  Send your file securely
                </h1>
                <p style={{ fontSize: 14, color: 'var(--mid)', marginBottom: 28, lineHeight: 1.6 }}>
                  Your file is encrypted and delivered directly to your TalentMapper contact.
                </p>
              </>
            )}
            {renderContent()}
          </div>
        </div>
        <LegalFooter />
      </main>

      {/* Modals */}
      <Modal
        title="Wrong file type"
        open={wrongTypeModal}
        onOk={() => setWrongTypeModal(false)}
        onCancel={() => setWrongTypeModal(false)}
        okText="OK"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>That file type isn't accepted. Executables and server-side scripts cannot be uploaded.</p>
        <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>
          If you're unsure which format to use, contact{' '}
          <a href="mailto:support@thetalentmapper.com" style={{ color: 'var(--orange)' }}>
            support@thetalentmapper.com
          </a>.
        </p>
      </Modal>

      <Modal
        title="File too large"
        open={tooLargeModal}
        onOk={() => setTooLargeModal(false)}
        onCancel={() => setTooLargeModal(false)}
        okText="OK"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>Your file exceeds the <strong>{MAX_FILE_SIZE_MB} MB</strong> size limit. Please compress it or contact{' '}
          <a href="mailto:support@thetalentmapper.com" style={{ color: 'var(--orange)' }}>
            support@thetalentmapper.com
          </a>{' '}for help.</p>
      </Modal>

      <Modal
        title="Remove file?"
        open={removeConfirmModal}
        onOk={confirmRemove}
        onCancel={() => setRemoveConfirmModal(false)}
        okText="Yes, remove"
        okButtonProps={{ danger: true }}
        cancelText="Keep file"
      >
        <p>Are you sure you want to remove <strong>{selectedFile?.name}</strong>? You'll need to select a new file.</p>
      </Modal>

      <Modal
        title="Leave while uploading?"
        open={leaveModal}
        onOk={confirmLeave}
        onCancel={() => { setLeaveModal(false); setPendingLeave(null); }}
        okText="Yes, leave"
        okButtonProps={{ danger: true }}
        cancelText="Stay"
      >
        <p>Your upload is still in progress. If you leave now, the upload will be cancelled and your file won't be received.</p>
      </Modal>

      <Modal
        title="Change password"
        open={changePwModal}
        onCancel={() => setChangePwModal(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ paddingTop: 8 }}>
          <ChangePassword
            user={user}
            onPasswordChanged={() => setChangePwModal(false)}
            onCancel={() => setChangePwModal(false)}
            onForgotPassword={() => {
              setChangePwModal(false);
              signOut(auth);
            }}
          />
        </div>
      </Modal>
    </div>
  );
}

export default FileUpload;
